import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Seo } from '../components/Seo';
import { Cookie } from 'lucide-react';

const Row = ({ type, purpose, examples }) => (
  <tr className="border-b border-border align-top">
    <td className="py-2 pr-3 font-semibold whitespace-nowrap">{type}</td>
    <td className="py-2 pr-3 text-muted-foreground">{purpose}</td>
    <td className="py-2 text-muted-foreground">{examples}</td>
  </tr>
);

export default function Cookies() {
  const updated = 'June 2026';
  return (
    <Layout>
      <Seo title="Cookie Policy" path="/cookies"
        description="How FlightDeals.in uses cookies and similar technologies for functionality, analytics, affiliate attribution and advertising — and how to manage them." />
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center gap-2 text-primary-foreground/80 text-sm mb-1"><Cookie className="h-4 w-4" /> Legal</div>
          <h1 className="font-display text-3xl font-semibold">Cookie Policy</h1>
          <p className="text-primary-foreground/75 mt-1">Last updated: {updated}</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-sm leading-relaxed text-muted-foreground" data-testid="cookies-page">
        <p className="mb-6">
          FlightDeals.in uses cookies, pixels, local storage, SDKs, device fingerprinting and similar technologies
          ("cookies") to run the site, understand usage, personalise content, and — importantly — attribute
          referrals to our travel partners so we earn commissions that keep the service free. By continuing to use
          the site with non-essential cookies enabled, you consent to their use as described here and in our{' '}
          <Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link>.
        </p>

        <h2 className="font-display text-xl font-semibold mb-3 text-foreground">Categories of cookies we use</h2>
        <div className="overflow-x-auto rounded-xl border border-border mb-6">
          <table className="w-full text-left text-xs sm:text-sm">
            <thead className="bg-secondary/60 text-foreground">
              <tr><th className="py-2 px-3">Category</th><th className="py-2 px-3">Purpose</th><th className="py-2 px-3">Examples</th></tr>
            </thead>
            <tbody className="px-3">
              <Row type="Strictly necessary" purpose="Core functionality, security, load balancing, saving your search state." examples="Session, CSRF, preferences (currency), local storage of recent searches." />
              <Row type="Analytics" purpose="Measure traffic, performance and how features are used to improve them." examples="Page views, events, A/B test buckets." />
              <Row type="Affiliate & attribution" purpose="Track clicks/redirects to partners so commissions are credited to us." examples="Referral IDs, click tokens, partner tracking pixels." />
              <Row type="Advertising & marketing" purpose="Personalise and measure ads and remarketing across sites." examples="Ad network IDs, conversion pixels, audience tags." />
            </tbody>
          </table>
        </div>

        <h2 className="font-display text-xl font-semibold mb-2 text-foreground">Third-party cookies</h2>
        <p className="mb-6">Some cookies are set by our partners and providers — including analytics and advertising networks and travel affiliate networks (such as Travelpayouts/Aviasales, Agoda and Viator). These parties may combine this information with other data they hold and use it under their own privacy policies.</p>

        <h2 className="font-display text-xl font-semibold mb-2 text-foreground">Managing your choices</h2>
        <ul className="list-disc pl-5 space-y-1 mb-6">
          <li>Adjust or block cookies in your browser settings (blocking essential cookies may break parts of the site).</li>
          <li>Use your device's ad-tracking controls and opt-out tools offered by advertising networks.</li>
          <li>Unsubscribe from marketing emails via the link in any message.</li>
          <li>Exercise privacy rights (including opting out of targeted advertising or "sale"/"sharing" where applicable) via our{' '}
            <Link to="/help" className="text-accent hover:underline">Help &amp; contact</Link> page.</li>
        </ul>

        <p>We may update this Cookie Policy from time to time; the "Last updated" date reflects the current version. For more on how we handle personal data, see our{' '}
          <Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link>.</p>
      </div>
    </Layout>
  );
}
